import React, { useState, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { extractExercisesFromImages } from '../../services/fitnessService';
import '../MenuImageImport.css';

/**
 * Photo capture / upload for PT-style exercise sheets; AI extraction + QR demo URLs.
 */
export default function ExerciseImageImport({ onExtractSuccess, onCancel }) {
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const handleFileSelect = useCallback(
    (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      const newFiles = files.slice(0, 5 - images.length);

      if (files.length > newFiles.length) {
        toast.error(`Maximum 5 images allowed. Only added ${newFiles.length} image(s).`);
      }

      const validFiles = newFiles.filter((file) => {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large. Maximum file size is 10MB.`);
          return false;
        }
        return true;
      });

      const newPreviews = validFiles.map((file) => URL.createObjectURL(file));

      setImages((prev) => [...prev, ...validFiles]);
      setPreviews((prev) => [...prev, ...newPreviews]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [images.length]
  );

  const removeImage = useCallback((index) => {
    URL.revokeObjectURL(previews[index]);
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  }, [previews]);

  const startCamera = async () => {
    try {
      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        });
      } catch (constraintError) {
        mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      }

      setStream(mediaStream);
      setShowCamera(true);

      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Camera access error:', error);
      if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No camera found. Please connect a camera or use file upload.');
      } else if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Camera access denied. Please allow camera permissions.');
      } else {
        toast.error('Unable to access camera. Try file upload.');
      }
    }
  };

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  }, [stream]);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        if (blob && images.length < 5) {
          try {
            const filename = `capture${Date.now()}.jpg`;
            let imageFile;
            try {
              imageFile = new File([blob], filename, { type: 'image/jpeg' });
            } catch (fileError) {
              imageFile = blob;
              imageFile.name = filename;
            }

            const preview = URL.createObjectURL(blob);

            setImages((prev) => [...prev, imageFile]);
            setPreviews((prev) => [...prev, preview]);

            toast.success('Photo captured!');
          } catch (error) {
            console.error('Error creating file from captured image:', error);
            toast.error('Failed to process captured image.');
          }
        } else if (images.length >= 5) {
          toast.error('Maximum 5 images allowed.');
        }
      },
      'image/jpeg',
      0.85
    );
  }, [images.length]);

  const handleExtract = async () => {
    if (images.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    setIsExtracting(true);
    setExtractionProgress('Uploading images...');

    try {
      const formData = new FormData();

      for (let i = 0; i < images.length; i++) {
        const file = images[i];
        const filename = file.name || `image${i + 1}.jpg`;
        formData.append('images', file, filename);
      }

      setExtractionProgress('Reading QR codes and analyzing with AI...');

      const data = await extractExercisesFromImages(formData);

      if (data.status !== 'success') {
        throw new Error(data.message || 'Extraction failed');
      }

      setExtractionProgress('Done');

      previews.forEach((u) => URL.revokeObjectURL(u));
      stopCamera();

      toast.success(
        data.exercises?.length
          ? `Found ${data.exercises.length} exercise(s)`
          : 'Extraction complete — review the form'
      );

      onExtractSuccess({
        exercises: data.exercises || [],
        decodedQrUrls: data.decodedQrUrls || [],
        confidence: data.confidence || null,
        usage: data.usage
      });
    } catch (error) {
      console.error('Exercise extraction error:', error);
      toast.error(error.message || 'Failed to extract exercises from images');
    } finally {
      setIsExtracting(false);
      setExtractionProgress('');
    }
  };

  const handleCancel = () => {
    previews.forEach((u) => URL.revokeObjectURL(u));
    stopCamera();
    onCancel();
  };

  return (
    <div className="menu-image-import">
      <div className="import-header">
        <h2>
          <i className="fas fa-camera"></i> Import exercises from photos
        </h2>
        <p className="import-description">
          Photograph a printed exercise sheet (for example from physical therapy). We read QR demo links when present
          and fill exercise details with AI. Review everything before saving.
        </p>
      </div>

      {showCamera && (
        <div className="camera-container">
          <video ref={videoRef} autoPlay playsInline muted className="camera-preview" />
          <div className="camera-controls">
            <button
              type="button"
              className="capture-btn"
              onClick={capturePhoto}
              disabled={images.length >= 5}
            >
              <i className="fas fa-camera"></i> Capture
            </button>
            <button type="button" className="close-camera-btn" onClick={stopCamera}>
              <i className="fas fa-times"></i> Close Camera
            </button>
          </div>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      )}

      {!showCamera && (
        <div className="upload-area">
          <div className="upload-options">
            <button
              type="button"
              className="upload-option-btn camera-btn"
              onClick={startCamera}
              disabled={images.length >= 5}
            >
              <i className="fas fa-camera"></i>
              <span>Take Photo</span>
            </button>

            <button
              type="button"
              className="upload-option-btn file-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={images.length >= 5}
            >
              <i className="fas fa-upload"></i>
              <span>Upload Images</span>
            </button>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          <p className="upload-hint">JPEG, PNG, WebP • Max 10MB each • Up to 5 images • Uses your menu scan quota</p>
        </div>
      )}

      {previews.length > 0 && (
        <div className="image-previews">
          <h3>Selected Images ({previews.length}/5)</h3>
          <div className="preview-grid">
            {previews.map((preview, index) => (
              <div key={index} className="preview-item">
                <img src={preview} alt={`Preview ${index + 1}`} />
                <button
                  type="button"
                  className="remove-preview-btn"
                  onClick={() => removeImage(index)}
                  title="Remove image"
                >
                  <i className="fas fa-times"></i>
                </button>
                <span className="preview-number">{index + 1}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {isExtracting && (
        <div className="extraction-progress">
          <div className="progress-spinner"></div>
          <p>{extractionProgress}</p>
        </div>
      )}

      <div className="import-tips">
        <h4>
          <i className="fas fa-lightbulb"></i> Tips
        </h4>
        <ul>
          <li>Include the full sheet so exercise names and reps are readable</li>
          <li>QR codes for demo videos work best with good lighting and focus</li>
          <li>You can upload multiple pages if your program spans sheets</li>
        </ul>
      </div>

      <div className="import-actions">
        <button type="button" className="cancel-btn" onClick={handleCancel} disabled={isExtracting}>
          Cancel
        </button>
        <button
          type="button"
          className="extract-btn"
          onClick={handleExtract}
          disabled={images.length === 0 || isExtracting}
        >
          {isExtracting ? (
            <>
              <span className="btn-spinner"></span>
              Extracting...
            </>
          ) : (
            <>
              <i className="fas fa-magic"></i>
              Extract exercises
            </>
          )}
        </button>
      </div>
    </div>
  );
}
